import React, {useMemo} from 'react';
import Intro from "../components/intro";
import AppPreview from "../components/AppPreview";
import {useTranslation} from "react-i18next";
import {Formik} from "formik";
import * as yup from "yup";
import InputFieldsSection from "../components/InputFieldsSection";
import FileFieldsSection from "../components/FileFieldsSection";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppPreviewV2 from "../components/AppPreviewV2";
import {v4 as uuidv4} from "uuid";
import {generateZipFromFormData, uploadToS3} from "../services/awsHandler";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";

const WhitelabelFormV2 = (props) => {
    const { } = props;
    const { t } = useTranslation();
    const navigate = useNavigate()

    const initialValues = useMemo(() => ({
        businessEmail: '',
        appName: "Olympus Top Fitness",
        secondAppName: '',
        thirdAppName: '',
        mainColor: "#192F51",
        secondaryColor: "#989696",
        textColor: '',
        appleIcon: null,
        androidIcon: null,
        notificationIcon: null,
        splash: null,
        logo: null,
        backgroundImage: null,
    }), []);

    const getFileValidation = (height, width) => {
        return yup.mixed()
            .required(t('mandatory-field'))
            .test('fileType', t('error-image-format'), (file) => {
                if(file && file.type === "image/png") return true
                else {
                    toast.error(t('error-image-format'));
                    return false
                }
            })
            .test('fileDimensions', t('error-image-size'), (file) => checkFileDimensions(file, height, width))
    }

    const checkFileDimensions = (file, height, width) => {
        if (!file) return false;
        if(!height || !width) return true
        return new Promise((resolve) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                if (img.width === width && img.height === height) {
                    resolve(true);
                } else {
                    toast.error(t('error-image-size'));
                    resolve(false);
                }
            };
        });
    }

    const validationSchema = useMemo(() => yup.object().shape({
        businessEmail: yup.string().email().required(t('mandatory-field')),
        appName: yup.string().matches(/^[A-Za-z0-9\s]+$/, t("english-only")).required(t('mandatory-field')),
        secondAppName: yup.string().matches(/^[A-Za-z0-9\s]+$/, t("english-only")).required(t('mandatory-field')),
        thirdAppName: yup.string().matches(/^[A-Za-z0-9\s]+$/, t("english-only")).required(t('mandatory-field')),
        mainColor: yup.string().matches(/^#([0-9a-f]{3}){1,2}$/i,  t("hex-color-only")).required(t('mandatory-field')),
        secondaryColor: yup.string().matches(/^#([0-9a-f]{3}){1,2}$/i, t("hex-color-only")).required(t('mandatory-field')),
        textColor: yup.string().required(t('mandatory-field')).oneOf(["#ffffff", "#000000"]),
        appleIcon: getFileValidation(1024, 1024),
        androidIcon: getFileValidation(512, 512),
        notificationIcon: getFileValidation(96, 96),
        splash: getFileValidation(2436, 1242),
        logo: getFileValidation(),
        backgroundImage: getFileValidation(500, 1024),
    }), []);

    const handleSubmit = async (values) => {
        try {
            const data = {
                businessEmail: values.businessEmail,
                appName: values.appName,
                secondAppName: values.secondAppName,
                thirdAppName: values.thirdAppName,
                mainColor: values.mainColor,
                textColor: values.textColor,
                secondaryColor: values.secondaryColor,
                submitDate: dayjs().format('DD-MM-YYYY')
            };

            const formData = new FormData();
            let renamedFile = new File([values.appleIcon], 'appleIcon.png', { type: values.appleIcon.type });
            formData.append('appleIcon', renamedFile);
            renamedFile = new File([values.androidIcon], 'androidIcon.png', { type: values.androidIcon.type });
            formData.append('androidIcon', renamedFile);
            renamedFile = new File([values.notificationIcon], 'notificationIcon.png', { type: values.notificationIcon.type });
            formData.append('notificationIcon', renamedFile);
            renamedFile = new File([values.splash], 'splash.png', { type: values.splash.type });
            formData.append('splash', renamedFile);
            renamedFile = new File([values.logo], 'logo.png', { type: values.logo.type });
            formData.append('logo', renamedFile);
            renamedFile = new File([values.backgroundImage], 'backgroundImage.png', { type: values.backgroundImage.type });
            formData.append('backgroundImage', renamedFile);

            const folder = `${values.appName}---${uuidv4()}`;
            const params = {
                Bucket: process.env.REACT_APP_BUCKET_NAME,
                Key: `${folder}/appDetails.json`,
                Body: JSON.stringify(data),
                ACL: 'private',
                ContentType: 'application/json'
            };

            uploadToS3(params)

            const imageParams = {
                Bucket: process.env.REACT_APP_BUCKET_NAME,
                ACL: 'private',
            };

            for (const key of formData.keys()) {
                if (formData.get(key)) {
                    const imageFile = formData.get(key);
                    imageParams.Key = `${folder}/${imageFile.name}`;
                    imageParams.Body = imageFile;
                    imageParams.ContentType = imageFile.type;
                    uploadToS3(imageParams)
                }
            }

            const zipFile = await generateZipFromFormData(formData, JSON.stringify(data));

            // Upload ZIP file to S3
            const zipParams = {
                Bucket: process.env.REACT_APP_BUCKET_NAME,
                Key: `${folder}/whitelabel.zip`,
                Body: zipFile,
                ACL: 'private',
                ContentType: 'application/zip'
            };

            uploadToS3(zipParams);
            navigate('/success')
        } catch (error) {
            console.error('Error sending data:', error);
        }
    };

    return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({handleSubmit, values, ...rest}) => (
            <div className="branded-app-main-wrapper">
                <Intro/>
                <InputFieldsSection values={values} {...rest}/>
                <FileFieldsSection values={values} {...rest}/>
                <AppPreviewV2 values={values} {...rest}/>
                <div className="actions-btn-wrapper">
                    <button className="continue-btn" onClick={handleSubmit}>{t('confirm')}</button>
                </div>
                <ToastContainer />
            </div>
        )}
    </Formik>
    );
};

export default WhitelabelFormV2;